import Nullable from "@data-force/app/interfaces/nullable";
import React from "react";
import {connect} from "react-redux";
import {Button, Form, Modal} from "antd";
import {
    EditorType,
    ElementsValuesMap,
    PresentationElementType,
    SchemaElementsFactory,
    UIElementType,
    updateTaskElementsValues,
    UpdateTaskElementsValuesAction,
    ValidationLevel,
} from "@data-force/app";
import "./TaskContainer.scss";
import {ElementsValuesType} from "@data-force/app/interfaces/store/actions/payload/schema/schemaElements";
import {SchemaState} from "@data-force/app/interfaces/store/reducers/schema";
import {UploadFile} from "antd/lib/upload/interface";
import {UpdateElementValuesData} from "@data-force/app/interfaces/store/actions/payload/schema";
import {getTranslate, Translate, TranslateFunction} from "react-localize-redux";
import StoreState from "../../../interfaces/store/storeState";
import {Task, TaskEntity} from "../../../interfaces/store/actions/payload/task";
import TextElement from "../../DynamicForm/TextElement/TextElement";
import TextAreaElement from "../../DynamicForm/TextAreaElement/TextAreaElement";
import RadioElement from "../../DynamicForm/RadioElement/RadioElement";
import CheckboxElement from "../../DynamicForm/CheckboxElement/CheckboxElement";
import ComboElement from "../../DynamicForm/ComboElement/ComboElement";
import MultiselectElement from "../../DynamicForm/MultiSelectElement/MultiselectElement";
import LiteralElement from "../../DynamicForm/LiteralElement/LiteralElement";
import Container from "../../DynamicForm/Container/Container";
import RepeatableActions from "../../DynamicForm/RepeatableActions/RepeatableActions";
import HistoryParams from "../../../interfaces/historyParams";
import {
    getHistoryParams, isPhaseExpired, isTaskChangeable, mergeUploadBuckets,
} from "../../../utils/common";
import {JobState} from "../../../interfaces/store/reducers/jobState";
import SingleFilePicker from "../../FilePicker/SingleFilePicker";
import {ShowResetTaskDialogAction} from "../../../interfaces/store/actions/layout";
import {showResetTaskDialog} from "../../../store/actions/layout";
import {resetTaskData, saveActiveTask} from "../../../store/actions/task";
import {ResetTaskDataAction, SaveActiveTaskAction} from "../../../interfaces/store/actions/task";
import {UploadsBucket} from "../../../interfaces/store/reducers/uploadFilesState";
import NumberElement from "../../DynamicForm/NumberElement/NumberElement";
import RejectionReason from "../../RejectionReason/RejectionReason";

interface StateProps {
    editor: Nullable<EditorType>
    schemaKey: string
    tasks: TaskEntity[]
    activeTaskFile: Nullable<UploadFile>
    elementsValuesMap: ElementsValuesMap
    taskResetDialogVisible: boolean
    translate: TranslateFunction
    activeTask: Nullable<Task>
    isReadOnly: boolean
}

interface OwnProps {
    task: TaskEntity
}

interface DispatchProps {
    saveActiveTask(payload: Task): SaveActiveTaskAction

    updateTaskElementsValues(payload: UpdateElementValuesData): UpdateTaskElementsValuesAction

    showResetTaskDialog(payload: boolean): ShowResetTaskDialogAction

    resetTaskData(): ResetTaskDataAction
}

type Props = StateProps & OwnProps & DispatchProps;

class TaskContainer extends React.Component<Props> {
    private static readonly translatePath: string = "components.TaskContainer.TaskContainer";

    constructor(props: Props) {
        super(props);

        this.onSave = this.onSave.bind(this);
        this.resetTaskData = this.resetTaskData.bind(this);
        this.resetTaskDialogHandler = this.resetTaskDialogHandler.bind(this);
    }

    /**
     * Save active task if status can be changed
     */
    onSave(): void {
        const {activeTask, task: {status}}: Props = this.props;
        const taskChangeable: boolean = isTaskChangeable(status);
        if (taskChangeable && activeTask) {
            this.props.saveActiveTask(activeTask);
        }
    }

    /**
     * Reset task data
     */
    resetTaskData(): void {
        this.props.resetTaskData();
    }

    /**
     * Show/hide reset task dialog handler
     */
    resetTaskDialogHandler(): void {
        const {taskResetDialogVisible}: StateProps = this.props;
        this.props.showResetTaskDialog(!taskResetDialogVisible);
    }

    render() {
        const {
            editor, schemaKey, task, taskResetDialogVisible, translate, isReadOnly,
        }: Props = this.props;
        const {translatePath}: typeof TaskContainer = TaskContainer;
        const {id: taskId, status}: TaskEntity = task;
        const isDisabled: boolean = !isTaskChangeable(status) || isReadOnly;

        return (
            <div
                className="df-tasks-container"
                key={taskId}
            >
                <SingleFilePicker/>
                {!!editor?.spanReferences?.length && (
                    <div className="df-task-metadata">
                        <RejectionReason/>
                        <h2 className="df-section-title">
                            <Translate id={`${translatePath}.sectionTitle.text`}/>
                            <span className="df-task-action-container">
                                <Button
                                    className="df-clear-fields"
                                    onClick={this.resetTaskDialogHandler}
                                    disabled={isDisabled}
                                >
                                    <Translate id={`${translatePath}.clearFields.text`}/>
                                </Button>
                            </span>
                        </h2>
                        <div className="df-metadata-form">
                            <Form
                                layout="vertical"
                                className="df-dynamic-form"
                            >
                                <SchemaElementsFactory
                                    element={editor!}
                                    references={editor!.spanReferences!}
                                    schemaKey={schemaKey}
                                    initFallbackValues
                                    validationLevel={ValidationLevel.FORM}
                                    metadataId={taskId}
                                    elementsValuesType={ElementsValuesType.TASK}
                                    uiElements={{
                                        [UIElementType.TEXT]: TextElement,
                                        [UIElementType.TEXTAREA]: TextAreaElement,
                                        [UIElementType.RADIO]: RadioElement,
                                        [UIElementType.CHECKBOX]: CheckboxElement,
                                        [UIElementType.COMBO]: ComboElement,
                                        [UIElementType.MULTISELECT]: MultiselectElement,
                                        [UIElementType.LITERAL]: LiteralElement,
                                        [UIElementType.INT]: NumberElement,
                                    }}
                                    presentationElements={{
                                        [PresentationElementType.CONTAINER]: Container,
                                        [PresentationElementType.REPEATABLE_ACTIONS]: RepeatableActions,
                                    }}
                                    onSave={this.onSave}
                                    metadataReadOnly={isDisabled}
                                />
                            </Form>
                        </div>
                    </div>
                )}
                <Modal
                    onCancel={this.resetTaskDialogHandler}
                    onOk={this.resetTaskData}
                    okText={translate(`${TaskContainer.translatePath}.confirmBtn.text`)}
                    cancelText={translate(`${TaskContainer.translatePath}.cancelBtn.text`)}
                    visible={taskResetDialogVisible}
                >
                    <p>
                        <Translate id={`${translatePath}.clearFieldsDialog.text`}/>
                        <br/>
                        <Translate id={`${translatePath}.clearFieldsQuestion.text`}/>
                    </p>
                </Modal>
            </div>
        );
    }
}

function mapStateToProps({
    schema, project, job, uploadFiles: {selectedFiles}, layout, localize,
}: StoreState): StateProps {
    const historyParams: Nullable<HistoryParams> = getHistoryParams();
    const schemaKey: string = `${project.projectDetails?.schemaId}_${historyParams?.phase}`;
    const {parsedSchemas}: SchemaState = schema;
    const {tasks, activeTask, jobPhase}: JobState = job;
    const taskId: string = activeTask?.id || "";
    const uploadBucket: UploadsBucket = mergeUploadBuckets(selectedFiles);

    return {
        editor: parsedSchemas[schemaKey] as EditorType,
        schemaKey,
        tasks,
        activeTaskFile: uploadBucket[taskId],
        elementsValuesMap: schema.elementsValuesMap,
        taskResetDialogVisible: layout.tasksSection.showResetTaskDialog,
        translate: getTranslate(localize),
        activeTask,
        isReadOnly: isPhaseExpired(jobPhase.accessStatus),
    };
}

const mapDispatchToProps: DispatchProps = {
    saveActiveTask,
    updateTaskElementsValues,
    showResetTaskDialog,
    resetTaskData,
};

export default connect(mapStateToProps, mapDispatchToProps)(TaskContainer);
